/**
 * Shuttle Step 1 JavaScript
 * 
 * Handles form interactions for shuttle booking step 1
 * 
 * @package A2A_Theme
 */

jQuery(document).ready(function($) {
    'use strict';
    
    /**
     * Step 1 Form Handler
     */
    class ShuttleStep1Handler {
        constructor() {
            this.form = $('#shuttle-step-1-form');
            this.manager = window.shuttleBookingManager;
            this.isInitialized = false;
            this.isRestoringData = false;
            
            // Form elements
            this.elements = {
                transferTypeRadios: $('input[name="transfer_type"]'),
                fromSelectDesktop: $('#shuttle-from-select'),
                toSelectDesktop: $('#shuttle-to-select'),
                fromSelectMobile: $('#shuttle-from-select-mobile'),
                toSelectMobile: $('#shuttle-to-select-mobile'),
                outboundDateDesktop: $('#shuttle-outbound-date'),
                returnDateDesktop: $('#shuttle-return-date'),
                outboundDateMobile: $('#shuttle-outbound-date-mobile'),
                returnDateMobile: $('#shuttle-return-date-mobile'),
                addReturnBtnDesktop: $('#shuttle-add-return-btn'),
                addReturnBtnMobile: $('#shuttle-add-return-btn-mobile'),
                passengersDesktop: $('#shuttle-passengers'),
                passengersMobile: $('#shuttle-passengers-mobile'),
                passengersModal: $('#shuttle-passengers-modal'),
                submitButton: this.form.find('button[type="submit"]')
            };
            
            // Load translatable strings from DOM (Weglot-compatible)
            this.translatableStrings = this.loadTranslatableStrings();
            
            this.init();
        }
        
        /**
         * Load translatable strings from hidden DOM elements
         * This allows Weglot to translate them
         */
        loadTranslatableStrings() {
            const container = document.getElementById('shuttle-translatable-strings');
            if (!container) {
                console.warn('Translatable strings container not found');
                return {};
            }
            
            const strings = {};
            const spans = container.querySelectorAll('span[data-key]');
            spans.forEach(span => {
                const key = span.getAttribute('data-key');
                const text = span.textContent;
                strings[key] = text;
            });
            
            return strings;
        }
        
        /**
         * Initialize step 1 handler
         */
        async init() {
            if (this.isInitialized) return;
            
            try {
                // Add body class for styling
                $('body').addClass('shuttle-booking-page');
                
                // Wait for booking manager to be ready
                await this.waitForManager();
                
                // Check and clear completed bookings before restoring
                this.clearCompletedBookingData();
                
                // Restore form state from saved data
                this.restoreFormState();

                // Restore Shuttle Step 1 Form
                this.resetShuttleStep1Form();
                
                // Initialize form components
                this.initializeSelect2();
                this.initializeDateRangePickers();
                this.setupEventListeners();
                
                // Load destinations into dropdowns
                await this.populateDestinations();
                
                // Restore From/To selections after destinations are loaded
                await this.restoreLocationSelections();
                
                // Update floating labels after everything is restored
                setTimeout(() => {
                    this.updateFloatingLabels();
                }, 500); // Give Select2 time to render
                
                this.isInitialized = true;
                
            } catch (error) {
                console.error('Failed to initialize Shuttle Step 1:', error);
                this.showError('Failed to initialize booking form. Please refresh the page.');
            }
        }
        
        /**
         * Clear completed booking data when starting a new booking session
         * This prevents old booking data from interfering with new bookings
         * NOTE: Reference-based storage (shuttle_booking_ref_*) is NOT cleared here
         *       as it allows users to revisit old thank you pages
         */
        clearCompletedBookingData() {
            try {
                const completionStr = localStorage.getItem('shuttle_booking_completed');
                
                if (!completionStr) {
                    // Still run cleanup for expired references
                    this.cleanupExpiredReferences();
                    return;
                }
                
                const completion = JSON.parse(completionStr);
                
                if (completion.completed) {
                    // Clear session-based storage (current booking in progress)
                    localStorage.removeItem('shuttle_booking_data');
                    localStorage.removeItem('shuttle_checkout_data');
                    localStorage.removeItem('shuttle_order_data');
                    localStorage.removeItem('shuttle_booking_completed');
                    
                    // Note: We do NOT clear shuttle_booking_ref_* keys
                    // These are preserved for 30 days to allow revisiting thank you pages
                    
                    // Reset the booking manager's data to defaults
                    if (this.manager) {
                        this.manager.bookingData = this.manager.getDefaultBookingData();
                    }
                }
                
                // Clean up expired reference storage
                this.cleanupExpiredReferences();
                
            } catch (e) {
                // Error clearing completed booking data
                // Non-critical error, continue with initialization
            }
        }
        
        /**
         * Clean up expired reference storage entries
         * Removes booking references older than 30 days
         */
        cleanupExpiredReferences() {
            try {
                const now = Date.now();
                const keysToRemove = [];
                
                // Scan all localStorage keys for expired references
                for (let i = 0; i < localStorage.length; i++) {
                    const key = localStorage.key(i);
                    
                    if (key && key.startsWith('shuttle_booking_ref_')) {
                        try {
                            const data = JSON.parse(localStorage.getItem(key));
                            
                            // Remove if expired (older than 30 days)
                            if (data.expiresAt && now > data.expiresAt) {
                                keysToRemove.push(key);
                            }
                        } catch (e) {
                            // Invalid JSON - remove it
                            keysToRemove.push(key);
                        }
                    }
                }
                
                // Remove expired entries
                keysToRemove.forEach(key => {
                    localStorage.removeItem(key);
                });
            } catch (e) {
                // Error cleaning up expired references
            }
        }
        
        /**
         * Wait for booking manager to be ready
         */
        async waitForManager() {
            let attempts = 0;
            const maxAttempts = 50; // 5 seconds
            
            while (!this.manager && attempts < maxAttempts) {
                await new Promise(resolve => setTimeout(resolve, 100));
                this.manager = window.shuttleBookingManager;
                attempts++;
            }
            
            if (!this.manager) {
                throw new Error('Booking manager not available');
            }
            
            // Wait for destinations to load
            attempts = 0;
            while (!this.manager.destinations && attempts < maxAttempts) {
                await new Promise(resolve => setTimeout(resolve, 100));
                attempts++;
            }
            
            if (!this.manager.destinations) {
                throw new Error('Destinations not loaded');
            }
            
            // Wait for availability so the datepickers are built with the real
            // weekday restrictions. Non-fatal: getAllowedWeekdays() fails open.
            attempts = 0;
            while (!this.manager.availability && attempts < maxAttempts) {
                await new Promise(resolve => setTimeout(resolve, 100));
                attempts++;
            }
        }
        
        /**
         * Restore form state from saved booking data
         */
        restoreFormState() {
            const data = this.manager.bookingData;
            
            // Transfer type
            this.elements.transferTypeRadios.filter(`[value="${data.transferType}"]`).prop('checked', true);
            this.updateTransferTypeUI(data.transferType);
            
            // Passengers
            this.updatePassengerDisplay();
        }

        /**
         * Restore Shuttle Step 1 Form
         */
        resetShuttleStep1Form() {
            const form = this.form;
            const button = this.elements.submitButton
            // Detect navigation via browser back/forward (cached or history)
            window.addEventListener('pageshow', function(event) {
                // Detect BFCache restore (Safari/iOS/Chrome)
                if (event.persisted || performance.getEntriesByType('navigation')[0]?.type === 'back_forward') {
                    if (form.hasClass('shuttle-step-1__form--loading')) {
                        form.removeClass('shuttle-step-1__form--loading');
                        button.prop('disabled', false);
                    }
                }
            });
        }
        
        /**
         * Restore location selections after destinations are loaded
         * Priority: 1) User's manual selections from localStorage, 2) Shuttle route ACF fields (not saved)
         */
        async restoreLocationSelections() {
            const data = this.manager.bookingData;
            
            // Set flag to prevent resetting during restoration
            this.isRestoringData = true;
            
            // Determine which data to use for prefilling
            let prefillFrom = null;
            let prefillTo = null;
            let isFromUserSelection = false;
            let isToUserSelection = false;
            
            // Priority 1: Check localStorage data (only if user has manually selected)
            // We check if the data exists AND if it's from a user selection (has both from AND to)
            if (data.from && data.from.id && data.to && data.to.id) {
                // User has made a complete selection - use it
                prefillFrom = data.from.id;
                prefillTo = data.to.id;
                isFromUserSelection = true;
                isToUserSelection = true;
            } else {
                // No complete user selection - check shuttle route prefill data
                if (window.shuttleRoutePrefill && window.shuttleRoutePrefill.isShuttleRoute) {
                    if (window.shuttleRoutePrefill.from) {
                        prefillFrom = window.shuttleRoutePrefill.from;
                    }
                    if (window.shuttleRoutePrefill.to) {
                        prefillTo = window.shuttleRoutePrefill.to;
                    }
                }
            }
            
            // Restore From selection if we have data (from either source)
            if (prefillFrom) {
                // Convert destinations to flat array
                // Destinations is an object with { airports: [...], resorts: [...] }
                let destinationsArray = [];
                if (Array.isArray(this.manager.destinations)) {
                    destinationsArray = this.manager.destinations;
                } else if (this.manager.destinations && typeof this.manager.destinations === 'object') {
                    // Flatten the nested arrays (airports and resorts)
                    destinationsArray = Object.values(this.manager.destinations).flat();
                }
                
                const fromDestination = destinationsArray.find(d => d.id === prefillFrom);
                
                if (!fromDestination) {
                    return;
                }
                
                // IMPORTANT: Only update bookingData if this is a user selection
                // ACF prefills should NOT be saved to localStorage
                if (isFromUserSelection) {
                    this.manager.updateBookingData({
                        from: {
                            id: fromDestination.id,
                            label: fromDestination.label || fromDestination.name
                        }
                    });
                }
                
                // Set From value in both desktop and mobile (visual only for ACF prefills)
                this.elements.fromSelectDesktop.val(prefillFrom).trigger('change');
                this.elements.fromSelectMobile.val(prefillFrom).trigger('change');
                
                // Show floating label
                $('.shuttle-step-1__field--from .shuttle-step-1__floating-label').addClass('show');
                
                // Load To options based on From selection (AWAIT this!)
                await this.updateToDestinations(prefillFrom);
                
                // Restore To selection if we have data (from either source)
                if (prefillTo) {
                    // Update bookingData with To selection (use array from above)
                    const toDestination = destinationsArray.find(d => d.id === prefillTo);
                    
                    if (toDestination) {
                        // IMPORTANT: Only update bookingData if this is a user selection
                        // ACF prefills should NOT be saved to localStorage
                        if (isToUserSelection) {
                            this.manager.updateBookingData({
                                to: {
                                    id: toDestination.id,
                                    label: toDestination.label || toDestination.name
                                }
                            });
                        }
                        
                        // Set To value in both desktop and mobile (visual only for ACF prefills)
                        this.elements.toSelectDesktop.val(prefillTo).trigger('change');
                        this.elements.toSelectMobile.val(prefillTo).trigger('change');
                        
                        // Show floating label
                        $('.shuttle-step-1__field--to .shuttle-step-1__floating-label').addClass('show');
                    }
                }
            }
            
            // Clear restoration flag
            setTimeout(() => {
                this.isRestoringData = false;
            }, 200);
            
            // Restore date selections
            if (data.outboundDate) {
                this.elements.outboundDateDesktop.val(data.outboundDate);
                this.elements.outboundDateMobile.val(data.outboundDate);
                $('.shuttle-step-1__field--outbound-date .shuttle-step-1__date-floating-label').addClass('show');
                $('.shuttle-step-1__input--date[name*="outbound"]').addClass('has-value');
                
                // Update return date picker minimum based on restored outbound date
                const outboundMoment = moment(data.outboundDate, 'DD.MM.YY, HH:mm');
                if (outboundMoment.isValid()) {
                    this.updateReturnDateMinimum(outboundMoment);
                }
            }
            
            if (data.returnDate) {
                this.elements.returnDateDesktop.val(data.returnDate);
                this.elements.returnDateMobile.val(data.returnDate);
                $('.shuttle-step-1__field--return-date .shuttle-step-1__date-floating-label').addClass('show');
                $('.shuttle-step-1__input--date[name*="return"]').addClass('has-value');
            }
        }
        
        /**
         * Initialize Select2 dropdowns
         */
        initializeSelect2() {
            const select2Options = {
                placeholder: function() {
                    return $(this).data('placeholder') || 'Select...';
                },
                allowClear: false,
                width: '100%',
                dropdownParent: $(document.body), // Prevent z-index issues
                templateResult: this.formatDestinationOption.bind(this),
                templateSelection: this.formatDestinationSelection.bind(this),
                escapeMarkup: function(markup) { return markup; } // Allow HTML
                // dropdownCssClass removed - not supported in this Select2 version
            };
            
            // Add custom event handler for optgroup styling after dropdown opens
            const self = this;
            
            // Get translated placeholder texts
            // Read from the first option's text (which Weglot has already translated)
            const fromPlaceholder = this.elements.fromSelectDesktop.find('option:first').text() || 'From';
            const toPlaceholder = this.elements.toSelectDesktop.find('option:first').text() || 'To';
            
            // Initialize from selects
            this.elements.fromSelectDesktop.select2({
                ...select2Options,
                placeholder: fromPlaceholder
            }).on('select2:open', function() {
                self.handleSelect2Open();
                self.styleOptgroupHeaders();
                self.setupSearchEventListeners();
            });
            
            this.elements.fromSelectMobile.select2({
                ...select2Options,
                placeholder: fromPlaceholder
            }).on('select2:open', function() {
                self.handleSelect2Open();
                self.styleOptgroupHeaders();
                self.setupSearchEventListeners();
            });
            
            // Initialize to selects (disabled initially)
            this.elements.toSelectDesktop.select2({
                ...select2Options,
                placeholder: toPlaceholder
            }).on('select2:open', function() {
                self.handleSelect2Open();
                self.styleOptgroupHeaders();
                self.setupSearchEventListeners();
            });
            
            this.elements.toSelectMobile.select2({
                ...select2Options,
                placeholder: toPlaceholder
            }).on('select2:open', function() {
                self.handleSelect2Open();
                self.styleOptgroupHeaders();
                self.setupSearchEventListeners();
            });
        }
        
        /**
         * Setup search event listeners for Select2 dropdowns
         */
        setupSearchEventListeners() {
            const self = this;
            
            // Remove any existing listeners to prevent duplicates
            $('.select2-search__field').off('input.shuttle-search keyup.shuttle-search');
            
            // Add search input listener
            $('.select2-search__field').on('input.shuttle-search keyup.shuttle-search', function() {
                // Use a small delay to allow Select2 to filter results first
                setTimeout(() => {
                    self.styleOptgroupHeaders();
                }, 10);
            });
            
            // Also listen for results being updated
            $(document).off('DOMNodeInserted.shuttle-results').on('DOMNodeInserted.shuttle-results', '.select2-results__options', function() {
                setTimeout(() => {
                    self.styleOptgroupHeaders();
                }, 10);
            });
        }
        
        /**
         * Handle Select2 open event for mobile fixes
         */
        handleSelect2Open() {
            $('.select2-container--open').addClass('shuttle-select2-class');
            
            // Check if we're on mobile (max-width: 520px)
            const isMobile = window.innerWidth <= 520;
            
            if (isMobile) {
                // Prevent body scroll when dropdown is open
                $('body').css('overflow', 'hidden');
                
                // Add ESC key handler for mobile
                this.setupMobileDropdownClose();
                
                // Prevent immediate selection on mobile by temporarily disabling clicks
                this.preventImmediateSelection();
            } else {
                // Only focus search field on desktop
                setTimeout(() => {
                    $('.select2-search__field').focus();
                }, 100);
                
                // Add extra positioning adjustment as fallback (only for non-mobile)
                setTimeout(() => {
                    this.adjustDropdownPositioning();
                }, 10);
            }
        }
        
        /**
         * Setup mobile dropdown close functionality
         */
        setupMobileDropdownClose() {
            const self = this;
            
            // Close on ESC key
            $(document).off('keydown.mobile-select2').on('keydown.mobile-select2', function(e) {
                if (e.keyCode === 27) { // ESC key
                    self.closeMobileDropdown();
                }
            });
            
            // Close when Select2 closes
            $(document).off('select2:close.mobile-select2').on('select2:close.mobile-select2', function() {
                self.closeMobileDropdown();
            });
        }
        
        /**
         * Close mobile dropdown and restore body scroll
         */
        closeMobileDropdown() {
            $('body').css('overflow', '');
            $(document).off('keydown.mobile-select2 select2:close.mobile-select2');
        }
        
        /**
         * Prevent immediate selection on mobile by adding a delay
         */
        preventImmediateSelection() {
            // Add a class to prevent clicks temporarily
            $('.select2-results').addClass('select2-mobile-opening');
            
            // Disable pointer events temporarily
            $('.select2-results__options').css('pointer-events', 'none');
            
            // Re-enable after a short delay
            setTimeout(() => {
                $('.select2-results').removeClass('select2-mobile-opening');
                $('.select2-results__options').css('pointer-events', 'auto');
            }, 300); // 300ms delay should be enough to prevent accidental selection
        }
        
        /**
         * Adjust dropdown positioning as a fallback to CSS
         */
        adjustDropdownPositioning() {
            $('.select2-dropdown').each(function() {
                const $dropdown = $(this);
                
                // Skip if already adjusted
                if ($dropdown.data('shuttle-positioned')) {
                    return;
                }
                
                // Get current position
                const currentTop = parseInt($dropdown.css('top')) || 0;
                const currentStyle = $dropdown.attr('style') || '';
                
                // Add spacing if positioned below (positive top value)
                if (currentTop > 0) {
                    const newTop = currentTop + 22;
                    
                    // Update inline styles
                    if (currentStyle.includes('top:')) {
                        const newStyle = currentStyle.replace(/top:\s*[^;]+;?/, `top: ${newTop}px;`);
                        $dropdown.attr('style', newStyle);
                    } else {
                        $dropdown.css('top', newTop + 'px');
                    }
                    
                    // Mark as positioned
                    $dropdown.data('shuttle-positioned', true);
                }
            });
        }
        
        /**
         * Style optgroup headers with icons after Select2 renders them
         */
        styleOptgroupHeaders() {
            // Wait a bit for Select2 to fully render
            setTimeout(() => {
                $('.select2-results__group').each(function() {
                    const $group = $(this);
                    let groupText = $group.text().trim();
                    
                    // Skip if already styled (check for our custom class)
                    if ($group.hasClass('shuttle-styled-group')) {
                        return;
                    }
                    
                    // If the group has been styled before but content changed, get original text
                    if ($group.find('.shuttle-select2-group-text').length > 0) {
                        groupText = $group.find('.shuttle-select2-group-text').text().trim();
                    }
                    
                    // Determine icon based on group text
                    const isAirports = groupText.toUpperCase().includes('AIRPORTS');
                    const iconSvg = isAirports ? 
                        '<svg width="20" height="20" viewBox="0 0 20 20" fill="none"><path d="M3.79866 2.33136L9.44497 8.03496L5.23928 10.5701L2.14257 9.73111L1 10.3851L3.70831 12.4195L3.57312 12.6759C3.455 12.8715 3.45863 13.0995 3.57046 13.2915C3.6823 13.4835 3.87963 13.6006 4.10958 13.597L4.39025 13.5904L4.79372 16.944L6.03359 16.2354L6.73811 13.1438L11.0443 10.7812L13.2152 18.5L14.668 17.6685L15.1433 8.46125L17.6838 7.05823C18.389 6.6286 19.3709 5.29852 18.8567 4.41565C18.3421 3.53201 16.6892 3.70991 15.9419 4.1126L13.4376 5.62295L5.25146 1.5L3.79866 2.33136Z" fill="#C2D6E7"/></svg>' :
                        '<svg width="20" height="20" viewBox="0 0 20 20" fill="none"><path d="M17.1834 7.04171C16.3084 3.19171 12.9501 1.45837 10.0001 1.45837C10.0001 1.45837 10.0001 1.45837 9.99175 1.45837C7.05008 1.45837 3.68341 3.18337 2.80841 7.03337C1.83341 11.3334 4.46675 14.975 6.85008 17.2667C7.73341 18.1167 8.86667 19.1667 10 19.1667C11.1333 19.1667 12.2667 18.1167 13.1417 17.2667C15.5251 14.975 18.1584 11.3417 17.1834 7.04171ZM10.0001 11.2167C8.55008 11.2167 7.37508 10.0417 7.37508 8.59171C7.37508 7.14171 8.55008 5.96671 10.0001 5.96671C11.4501 5.96671 12.6251 7.14171 12.6251 8.59171C12.6251 10.0417 11.4501 11.2167 10.0001 11.2167Z" fill="#C2D6E7"/></svg>';
                    
                    // Mark as styled and wrap the content with icon
                    $group.addClass('shuttle-styled-group').html(`
                        <div class="shuttle-select2-group-header">
                            <div class="shuttle-select2-group-icon">${iconSvg}</div>
                            <span class="shuttle-select2-group-text">${groupText}</span>
                        </div>
                    `);
                });
                
                // Also handle any groups that might have been reset by Select2 during search
                $('.select2-results__group:not(.shuttle-styled-group)').each(function() {
                    const $group = $(this);
                    const groupText = $group.text().trim();
                    
                    if (groupText) {
                        // Determine icon based on group text
                        const isAirports = groupText.toUpperCase().includes('AIRPORTS');
                        const iconSvg = isAirports ? 
                            '<svg width="20" height="20" viewBox="0 0 20 20" fill="none"><path d="M3.79866 2.33136L9.44497 8.03496L5.23928 10.5701L2.14257 9.73111L1 10.3851L3.70831 12.4195L3.57312 12.6759C3.455 12.8715 3.45863 13.0995 3.57046 13.2915C3.6823 13.4835 3.87963 13.6006 4.10958 13.597L4.39025 13.5904L4.79372 16.944L6.03359 16.2354L6.73811 13.1438L11.0443 10.7812L13.2152 18.5L14.668 17.6685L15.1433 8.46125L17.6838 7.05823C18.389 6.6286 19.3709 5.29852 18.8567 4.41565C18.3421 3.53201 16.6892 3.70991 15.9419 4.1126L13.4376 5.62295L5.25146 1.5L3.79866 2.33136Z" fill="#C2D6E7"/></svg>' :
                            '<svg width="20" height="20" viewBox="0 0 20 20" fill="none"><path d="M17.1834 7.04171C16.3084 3.19171 12.9501 1.45837 10.0001 1.45837C10.0001 1.45837 10.0001 1.45837 9.99175 1.45837C7.05008 1.45837 3.68341 3.18337 2.80841 7.03337C1.83341 11.3334 4.46675 14.975 6.85008 17.2667C7.73341 18.1167 8.86667 19.1667 10 19.1667C11.1333 19.1667 12.2667 18.1167 13.1417 17.2667C15.5251 14.975 18.1584 11.3417 17.1834 7.04171ZM10.0001 11.2167C8.55008 11.2167 7.37508 10.0417 7.37508 8.59171C7.37508 7.14171 8.55008 5.96671 10.0001 5.96671C11.4501 5.96671 12.6251 7.14171 12.6251 8.59171C12.6251 10.0417 11.4501 11.2167 10.0001 11.2167Z" fill="#C2D6E7"/></svg>';
                        
                        // Mark as styled and wrap the content with icon
                        $group.addClass('shuttle-styled-group').html(`
                            <div class="shuttle-select2-group-header">
                                <div class="shuttle-select2-group-icon">${iconSvg}</div>
                                <span class="shuttle-select2-group-text">${groupText}</span>
                            </div>
                        `);
                    }
                });
            }, 50);
        }
        
        /**
         * Format destination option in dropdown with icons
         */
        formatDestinationOption(option) {
            if (!option.id) {
                return option.text; // Return placeholder as-is
            }
            
            // Check if this is an optgroup
            if (option.element && option.element.tagName === 'OPTGROUP') {
                const type = $(option.element).data('type');
                const iconSvg = type === 'airports' ? 
                    '<svg width="20" height="20" viewBox="0 0 20 20" fill="none"><path d="M3.79866 2.33136L9.44497 8.03496L5.23928 10.5701L2.14257 9.73111L1 10.3851L3.70831 12.4195L3.57312 12.6759C3.455 12.8715 3.45863 13.0995 3.57046 13.2915C3.6823 13.4835 3.87963 13.6006 4.10958 13.597L4.39025 13.5904L4.79372 16.944L6.03359 16.2354L6.73811 13.1438L11.0443 10.7812L13.2152 18.5L14.668 17.6685L15.1433 8.46125L17.6838 7.05823C18.389 6.6286 19.3709 5.29852 18.8567 4.41565C18.3421 3.53201 16.6892 3.70991 15.9419 4.1126L13.4376 5.62295L5.25146 1.5L3.79866 2.33136Z" fill="#C2D6E7"/></svg>' :
                    '<svg width="20" height="20" viewBox="0 0 20 20" fill="none"><path d="M17.1834 7.04171C16.3084 3.19171 12.9501 1.45837 10.0001 1.45837C10.0001 1.45837 10.0001 1.45837 9.99175 1.45837C7.05008 1.45837 3.68341 3.18337 2.80841 7.03337C1.83341 11.3334 4.46675 14.975 6.85008 17.2667C7.73341 18.1167 8.86667 19.1667 10 19.1667C11.1333 19.1667 12.2667 18.1167 13.1417 17.2667C15.5251 14.975 18.1584 11.3417 17.1834 7.04171ZM10.0001 11.2167C8.55008 11.2167 7.37508 10.0417 7.37508 8.59171C7.37508 7.14171 8.55008 5.96671 10.0001 5.96671C11.4501 5.96671 12.6251 7.14171 12.6251 8.59171C12.6251 10.0417 11.4501 11.2167 10.0001 11.2167Z" fill="#C2D6E7"/></svg>';
                
                return `<div class="shuttle-select2-group-header">
                    <div class="shuttle-select2-group-icon">${iconSvg}</div>
                    <span class="shuttle-select2-group-text">${option.text}</span>
                </div>`;
            }
            
            // Regular option
            const dataType = $(option.element).data('type');
            const iconSvg = dataType === 'airport' ? 
                '<svg width="20" height="20" viewBox="0 0 20 20" fill="none"><path d="M3.79866 2.33136L9.44497 8.03496L5.23928 10.5701L2.14257 9.73111L1 10.3851L3.70831 12.4195L3.57312 12.6759C3.455 12.8715 3.45863 13.0995 3.57046 13.2915C3.6823 13.4835 3.87963 13.6006 4.10958 13.597L4.39025 13.5904L4.79372 16.944L6.03359 16.2354L6.73811 13.1438L11.0443 10.7812L13.2152 18.5L14.668 17.6685L15.1433 8.46125L17.6838 7.05823C18.389 6.6286 19.3709 5.29852 18.8567 4.41565C18.3421 3.53201 16.6892 3.70991 15.9419 4.1126L13.4376 5.62295L5.25146 1.5L3.79866 2.33136Z" fill="#C2D6E7"/></svg>' :
                '<svg width="20" height="20" viewBox="0 0 20 20" fill="none"><path d="M17.1834 7.04171C16.3084 3.19171 12.9501 1.45837 10.0001 1.45837C10.0001 1.45837 10.0001 1.45837 9.99175 1.45837C7.05008 1.45837 3.68341 3.18337 2.80841 7.03337C1.83341 11.3334 4.46675 14.975 6.85008 17.2667C7.73341 18.1167 8.86667 19.1667 10 19.1667C11.1333 19.1667 12.2667 18.1167 13.1417 17.2667C15.5251 14.975 18.1584 11.3417 17.1834 7.04171ZM10.0001 11.2167C8.55008 11.2167 7.37508 10.0417 7.37508 8.59171C7.37508 7.14171 8.55008 5.96671 10.0001 5.96671C11.4501 5.96671 12.6251 7.14171 12.6251 8.59171C12.6251 10.0417 11.4501 11.2167 10.0001 11.2167Z" fill="#C2D6E7"/></svg>';
            
            return `<div class="shuttle-select2-option">
                <div class="shuttle-select2-option-icon">${iconSvg}</div>
                <span class="shuttle-select2-option-text">${option.text}</span>
            </div>`;
        }
        
        /**
         * Format selected destination (no icons in selection)
         */
        formatDestinationSelection(option) {
            return option.text || option.id;
        }
        
        /**
         * Round time to next 15-minute increment
         * @param {moment} momentObj - Moment object to round
         * @returns {moment} Rounded moment object
         */
        roundToNext15Minutes(momentObj) {
            const minutes = momentObj.minutes();
            const remainder = minutes % 15;
            
            if (remainder === 0) {
                // Already on a 15-minute mark
                return momentObj.clone();
            }
            
            // Round up to next 15-minute mark
            const minutesToAdd = 15 - remainder;
            return momentObj.clone().add(minutesToAdd, 'minutes').seconds(0).milliseconds(0);
        }
        
        /**
         * Roll a moment forward day-by-day until it lands on an allowed weekday.
         * Used only for computed default/suggested dates (never to override an
         * explicit user selection). Preserves time-of-day.
         */
        getNextAllowedMoment(momentObj, allowedWeekdays) {
            const m = momentObj.clone();
            if (!Array.isArray(allowedWeekdays) || allowedWeekdays.length === 0) {
                return m;
            }
            let guard = 0;
            while (!allowedWeekdays.includes(m.isoWeekday()) && guard < 8) {
                m.add(1, 'day');
                guard++;
            }
            return m;
        }
        
        /**
         * Clear stored outbound/return dates whose weekday is no longer allowed
         * (availability may have changed since the user's last visit), so the
         * user consciously re-picks instead of being auto-booked onto a bad day.
         */
        clearStoredDatesIfWeekdayNotAllowed(allowedWeekdays) {
            if (!Array.isArray(allowedWeekdays) || allowedWeekdays.length === 0) {
                return;
            }
            
            const data = this.manager.bookingData;
            const updates = {};
            
            if (data.outboundDate) {
                const dow = this.manager.getDayOfWeekFromDate(data.outboundDate);
                if (!allowedWeekdays.includes(dow)) {
                    updates.outboundDate = '';
                    updates.outboundTime = '';
                    this.elements.outboundDateDesktop.val('').removeClass('has-value');
                    this.elements.outboundDateMobile.val('').removeClass('has-value');
                    $('.shuttle-step-1__field--outbound-date .shuttle-step-1__date-floating-label').removeClass('show');
                }
            }
            
            if (data.returnDate) {
                const dow = this.manager.getDayOfWeekFromDate(data.returnDate);
                if (!allowedWeekdays.includes(dow)) {
                    updates.returnDate = '';
                    updates.returnTime = '';
                    this.elements.returnDateDesktop.val('').removeClass('has-value');
                    this.elements.returnDateMobile.val('').removeClass('has-value');
                    $('.shuttle-step-1__field--return-date .shuttle-step-1__date-floating-label').removeClass('show');
                }
            }
            
            if (Object.keys(updates).length > 0) {
                this.manager.updateBookingData(updates);
            }
        }
        
        /**
         * Initialize DateRangePicker inputs
         */
        initializeDateRangePickers() {
            const data = this.manager.bookingData;
            
            // Allowed booking weekdays (ISO 1=Mon..7=Sun)
            const allowedWeekdays = this.manager.getAllowedWeekdays();
            
            // Define booking season date range (01.12.2026 - 15.04.2027)
            const seasonStartDate = moment('2026-12-01', 'YYYY-MM-DD').startOf('day');
            const seasonEndDate = moment('2027-04-15', 'YYYY-MM-DD').endOf('day');
            
            // Determine the actual minDate: use season start if we're before it, otherwise use current date
            const actualMinDate = moment().isBefore(seasonStartDate) ? seasonStartDate : moment();
            
            // Clear any stored dates whose weekday is no longer allowed (availability
            // may have changed since the user's last visit) so the user re-picks.
            this.clearStoredDatesIfWeekdayNotAllowed(allowedWeekdays);
            
            // Get translated locale strings from hidden div
            const strings = this.translatableStrings || {};
            
            // Base options for all datepickers
            const baseOptions = {
                locale: { 
                    format: 'DD.MM.YY, HH:mm', // Include time in format
                    firstDay: 1, // Monday as first day of week (0 = Sunday, 1 = Monday)
                    applyLabel: strings.applyLabel || 'Apply',
                    cancelLabel: strings.cancelLabel || 'Cancel',
                    customRangeLabel: strings.customRangeLabel || 'Select time',
                    monthNames: [
                        strings.monthJanuary || 'January',
                        strings.monthFebruary || 'February',
                        strings.monthMarch || 'March',
                        strings.monthApril || 'April',
                        strings.monthMay || 'May',
                        strings.monthJune || 'June',
                        strings.monthJuly || 'July',
                        strings.monthAugust || 'August',
                        strings.monthSeptember || 'September',
                        strings.monthOctober || 'October',
                        strings.monthNovember || 'November',
                        strings.monthDecember || 'December'
                    ],
                    daysOfWeek: [
                        strings.daySu || 'Su',
                        strings.dayMo || 'Mo',
                        strings.dayTu || 'Tu',
                        strings.dayWe || 'We',
                        strings.dayTh || 'Th',
                        strings.dayFr || 'Fr',
                        strings.daySa || 'Sa'
                    ]
                },
                containerClass: 'shuttle-daterangepicker',
                timePicker: true,
                timePicker24Hour: true,
                timePickerIncrement: 15, // 15-minute increments
                singleDatePicker: true,
                showDropdowns: true,
                minDate: actualMinDate,
                maxDate: seasonEndDate,
                autoUpdateInput: false, // Don't auto-fill the input
                // Read allowed weekdays at render time (not an init-time snapshot) so
                // the calendar greys out correctly even if availability resolved late
                isInvalidDate: (date) => !this.manager.getAllowedWeekdays().includes(date.isoWeekday())
            };
            
            // Outbound date picker options with stored date
            const outboundOptions = { ...baseOptions };
            if (data.outboundDate) {
                const outboundMoment = moment(data.outboundDate, 'DD.MM.YY, HH:mm');
                if (outboundMoment.isValid()) {
                    outboundOptions.startDate = outboundMoment;
                }
            } else {
                // No stored date - default to the next allowed weekday within the valid
                // range. Base on actualMinDate (season start or now) so the default is
                // never clamped by minDate onto a disallowed weekday.
                const defaultOutbound = this.getNextAllowedMoment(actualMinDate, allowedWeekdays);
                outboundOptions.startDate = this.roundToNext15Minutes(defaultOutbound);
            }
            
            // Return date picker options with stored date
            const returnOptions = { ...baseOptions };
            if (data.returnDate) {
                const returnMoment = moment(data.returnDate, 'DD.MM.YY, HH:mm');
                if (returnMoment.isValid()) {
                    returnOptions.startDate = returnMoment;
                    // Also set minimum date if outbound is selected (12h after outbound)
                    if (data.outboundDate) {
                        const outboundMoment = moment(data.outboundDate, 'DD.MM.YY, HH:mm');
                        if (outboundMoment.isValid()) {
                            returnOptions.minDate = moment(outboundMoment).add(12, 'hours');
                        }
                    }
                }
            } else if (data.outboundDate) {
                // No stored return date but have outbound - set to 12h after outbound, rounded
                const outboundMoment = moment(data.outboundDate, 'DD.MM.YY, HH:mm');
                if (outboundMoment.isValid()) {
                    const minReturnTime = moment(outboundMoment).add(12, 'hours');
                    returnOptions.startDate = this.roundToNext15Minutes(this.getNextAllowedMoment(minReturnTime, allowedWeekdays));
                    returnOptions.minDate = minReturnTime;
                }
            } else {
                // No stored dates at all - default to the next allowed weekday within the
                // valid range, rounded (based on actualMinDate to avoid clamping onto a
                // disallowed weekday).
                returnOptions.startDate = this.roundToNext15Minutes(this.getNextAllowedMoment(actualMinDate, allowedWeekdays));
            }
            
            // Initialize outbound date pickers
            this.elements.outboundDateDesktop.daterangepicker(outboundOptions, this.handleDateSelection.bind(this, 'outbound'));
            this.elements.outboundDateMobile.daterangepicker(outboundOptions, this.handleDateSelection.bind(this, 'outbound'));
            
            // Initialize return date pickers
            this.elements.returnDateDesktop.daterangepicker(returnOptions, this.handleDateSelection.bind(this, 'return'));
            this.elements.returnDateMobile.daterangepicker(returnOptions, this.handleDateSelection.bind(this, 'return'));
            
            // Handle Apply button clicks (when user clicks Apply without changing date/time)
            this.elements.outboundDateDesktop.on('apply.daterangepicker', this.handleDateApply.bind(this, 'outbound'));
            this.elements.outboundDateMobile.on('apply.daterangepicker', this.handleDateApply.bind(this, 'outbound'));
            this.elements.returnDateDesktop.on('apply.daterangepicker', this.handleDateApply.bind(this, 'return'));
            this.elements.returnDateMobile.on('apply.daterangepicker', this.handleDateApply.bind(this, 'return'));
            
            // Add show event handlers for scroll behavior (desktop only)
            this.elements.outboundDateDesktop.on('show.daterangepicker', (event, picker) => {
                this.scrollToDatepicker(picker);
                this.injectSelectTimeLabel(picker);
                this.setupDatepickerLabelObserver(picker);
            });
            this.elements.returnDateDesktop.on('show.daterangepicker', (event, picker) => {
                this.scrollToDatepicker(picker);
                this.injectSelectTimeLabel(picker);
                this.setupDatepickerLabelObserver(picker);
            });
            
            // Add show event handlers for mobile to inject label
            this.elements.outboundDateMobile.on('show.daterangepicker', (event, picker) => {
                this.injectSelectTimeLabel(picker);
                this.setupDatepickerLabelObserver(picker);
            });
            this.elements.returnDateMobile.on('show.daterangepicker', (event, picker) => {
                this.injectSelectTimeLabel(picker);
                this.setupDatepickerLabelObserver(picker);
            });
            
            // Add mobile overlay handling
            this.setupDatePickerMobileOverlay();
        }
        
        /**
         * Inject translated "Select time" label into datepicker
         * @param {Object} picker - DateRangePicker instance
         */
        injectSelectTimeLabel(picker) {
            // Wait for DOM to be ready
            setTimeout(() => {
                const calendarTime = picker.container.find('.calendar-time');
                if (calendarTime.length && !calendarTime.find('.calendar-time__label').length) {
                    const strings = this.translatableStrings || {};
                    const labelText = strings.customRangeLabel || 'Select time';
                    const label = $('<div class="calendar-time__label"></div>').text(labelText);
                    calendarTime.prepend(label);
                }
            }, 50);
        }
        
        /**
         * Setup mutation observer to re-inject label when datepicker re-renders
         * @param {Object} picker - DateRangePicker instance
         */
        setupDatepickerLabelObserver(picker) {
            const strings = this.translatableStrings || {};
            const labelText = strings.customRangeLabel || 'Select time';
            
            // Use MutationObserver to watch for DOM changes in the datepicker
            const observer = new MutationObserver(() => {
                const calendarTime = picker.container.find('.calendar-time');
                if (calendarTime.length && !calendarTime.find('.calendar-time__label').length) {
                    const label = $('<div class="calendar-time__label"></div>').text(labelText);
                    calendarTime.prepend(label);
                }
            });
            
            // Observe the datepicker container for changes
            if (picker.container[0]) {
                observer.observe(picker.container[0], {
                    childList: true,
                    subtree: true
                });
            }
        }
        
        /**
         * Setup mobile overlay for date picker
         */
        setupDatePickerMobileOverlay() {
            // Create overlay if it doesn't exist
            if (!$('.shuttle-daterangepicker-overlay').length) {
                $('<div class="shuttle-daterangepicker-overlay"></div>').appendTo('body');
            }
            
            // Handle datepicker show/hide
            $(document).on('show.daterangepicker', () => {
                $('body').addClass('shuttle-daterangepicker-open');
            });
            
            $(document).on('hide.daterangepicker apply.daterangepicker cancel.daterangepicker', () => {
                $('body').removeClass('shuttle-daterangepicker-open');
            });
            
            // Handle overlay click
            $('.shuttle-daterangepicker-overlay').on('click', () => {
                this.closeDatePickerOverlay();
            });
        }
        
        /**
         * Close datepicker overlay and cleanup
         */
        closeDatePickerOverlay() {
            $('.daterangepicker').each(function() {
                const picker = $(this).data('daterangepicker');
                if (picker) {
                    picker.hide();
                }
            });
            
            // Force remove overlay class as fallback
            setTimeout(() => {
                $('body').removeClass('shuttle-daterangepicker-open');
            }, 100);
        }
        
        /**
         * Scroll to datepicker to ensure Apply button is visible
         * Only on desktop (above 768px)
         */
        scrollToDatepicker(picker) {
            // Check if we're on desktop (above 768px)
            if (window.innerWidth <= 768) {
                return; // Mobile view - don't scroll
            }
            
            // Wait for datepicker to fully render and position
            setTimeout(() => {
                const $datepicker = $('.daterangepicker:visible');
                
                if ($datepicker.length === 0) {
                    return;
                }
                
                // Get datepicker position and dimensions
                const pickerOffset = $datepicker.offset();
                const pickerHeight = $datepicker.outerHeight();
                const pickerBottom = pickerOffset.top + pickerHeight;
                
                // Get viewport height
                const viewportHeight = $(window).height();
                const viewportScrollTop = $(window).scrollTop();
                const viewportBottom = viewportScrollTop + viewportHeight;
                
                // Check if Apply button area is out of view
                // Add some padding (100px) to ensure the button is comfortably visible
                const neededBottom = pickerBottom + 100;
                
                if (neededBottom > viewportBottom) {
                    // Calculate how much we need to scroll
                    const scrollAmount = neededBottom - viewportBottom;
                    
                    // Smooth scroll to show the Apply button
                    $('html, body').animate({
                        scrollTop: viewportScrollTop + scrollAmount
                    }, 300); // 300ms animation duration
                }
            }, 100); // Wait 100ms to ensure datepicker is fully positioned
        }
        
        /**
         * Setup event listeners
         */
        setupEventListeners() {
            // Transfer type change
            this.elements.transferTypeRadios.on('change', this.handleTransferTypeChange.bind(this));
            
            // From selection change
            this.elements.fromSelectDesktop.on('select2:select', this.handleFromSelection.bind(this));
            this.elements.fromSelectMobile.on('select2:select', this.handleFromSelection.bind(this));
            
            // To selection change
            this.elements.toSelectDesktop.on('select2:select', this.handleToSelection.bind(this));
            this.elements.toSelectMobile.on('select2:select', this.handleToSelection.bind(this));
            
            // Add return transfer buttons
            this.elements.addReturnBtnDesktop.on('click', this.handleAddReturnTransfer.bind(this));
            this.elements.addReturnBtnMobile.on('click', this.handleAddReturnTransfer.bind(this));
            
            // Passengers click
            $('.shuttle-step-1__passengers').on('click', this.showPassengersModal.bind(this));
            
            // Passengers modal events
            this.elements.passengersModal.find('.shuttle-step-1__modal-close').on('click', this.hidePassengersModal.bind(this));
            this.elements.passengersModal.find('.shuttle-step-1__counter-btn').on('click', this.handlePassengerCountChange.bind(this));
            this.elements.passengersModal.find('.shuttle-step-1__modal-apply').on('click', this.applyPassengerChanges.bind(this));
            
            // Input field events for direct number entry
            this.elements.passengersModal.find('input[data-type]').on('blur', this.handlePassengerInputChange.bind(this));
            this.elements.passengersModal.find('input[data-type]').on('keypress', this.handlePassengerInputKeypress.bind(this));
            
            // Return date validation - prevent opening return datepicker without outbound date
            $('#shuttle-return-date, #shuttle-return-date-mobile, [id*="shuttle-return-date"]').on('click focus', this.validateReturnDateAccess.bind(this));
            
            // Form submission
            this.form.on('submit', this.handleFormSubmit.bind(this));
            
            // Swap button (desktop only)
            $('.shuttle-step-1__swap-btn').on('click', this.handleSwapLocations.bind(this));
        }
        
        /**
         * Populate destination dropdowns
         */
        async populateDestinations() {
            const destinations = this.manager.destinations;
            if (!destinations) return;
            
            // Clear existing options and optgroups completely
            this.clearDropdownOptions(this.elements.fromSelectDesktop);
            this.clearDropdownOptions(this.elements.fromSelectMobile);
            
            // Sort airports and resorts separately
            const airports = (destinations.airports || []).sort((a, b) => a.label.localeCompare(b.label));
            const resorts = (destinations.resorts || []).sort((a, b) => a.label.localeCompare(b.label));
            
            // Add airports group
            if (airports.length > 0) {
                const airportsLabel = this.translatableStrings?.airports || 'AIRPORTS';
                const airportsGroup = $(`<optgroup label="${airportsLabel}" data-type="airports"></optgroup>`);
                airports.forEach(dest => {
                    const option = $(`<option value="${dest.id}" data-type="airport">${dest.label}</option>`);
                    airportsGroup.append(option);
                });
                this.elements.fromSelectDesktop.append(airportsGroup);
                this.elements.fromSelectMobile.append(airportsGroup.clone());
            }
            
            // Add resorts group
            if (resorts.length > 0) {
                const resortsLabel = this.translatableStrings?.resorts || 'RESORTS';
                const resortsGroup = $(`<optgroup label="${resortsLabel}" data-type="resorts"></optgroup>`);
                resorts.forEach(dest => {
                    const option = $(`<option value="${dest.id}" data-type="resort">${dest.label}</option>`);
                    resortsGroup.append(option);
                });
                this.elements.fromSelectDesktop.append(resortsGroup);
                this.elements.fromSelectMobile.append(resortsGroup.clone());
            }
            
            // Trigger change to update Select2
            this.elements.fromSelectDesktop.trigger('change');
            this.elements.fromSelectMobile.trigger('change');
            
        }
        
        /**
         * Handle transfer type change
         */
        handleTransferTypeChange(e) {
            const transferType = e.target.value;
            this.updateTransferTypeUI(transferType);
            
            // Update booking data
            this.manager.updateBookingData({ transferType });
        }
        
        /**
         * Update transfer type UI
         */
        updateTransferTypeUI(transferType) {
            const isReturn = transferType === 'return';
            
            // Update radio button labels
            $('.shuttle-step-1__radio-label').removeClass('shuttle-step-1__radio-label--selected');
            $(`.shuttle-step-1__radio-label:has(input[value="${transferType}"])`).addClass('shuttle-step-1__radio-label--selected');
            
            // Toggle form mode class
            this.form.toggleClass('shuttle-step-1__form--return-mode', isReturn);
            
            // Show/hide return date fields and add return buttons
            $('.shuttle-step-1__field--return-date').toggle(isReturn);
            $('.shuttle-step-1__field--add-return').toggle(!isReturn);
            
            // Update form class
            this.form.toggleClass('shuttle-step-1__form--return', isReturn);
        }
        
        /**
         * Handle "Add return transfer" button click
         */
        handleAddReturnTransfer(e) {
            e.preventDefault();
            
            // Validate that outbound date and time are selected first
            const outboundDate = this.manager.bookingData.outboundDate;
            const outboundTime = this.manager.bookingData.outboundTime;
            
            if (!outboundDate || !outboundTime) {
                // Show error message instead of switching to return mode
                const errorMsg = this.translatableStrings?.errorOutboundDateFirst || 'Please, select outbound date and time first';
                this.showValidationError(errorMsg);
                return;
            }
            
            // Clear any existing error
            this.hideValidationError();
            
            // Switch to return mode
            this.elements.transferTypeRadios.filter('[value="return"]').prop('checked', true).trigger('change');
            
            // Open return datepicker
            setTimeout(() => {
                this.elements.returnDateDesktop.focus().click();
            }, 100);
        }
        
        /**
         * Handle from location selection
         */
        async handleFromSelection(e) {
            const fromId = e.params.data.id;
            const fromLabel = e.params.data.text;
            
            // Clear validation error when user selects from location
            this.hideValidationError();
            
            // Update booking data
            const updateData = { from: { id: fromId, label: fromLabel } };
            
            // Only reset "to" selection if not restoring data
            if (!this.isRestoringData) {
                updateData.to = { id: '', label: '' }; // Reset to selection
            }
            
            this.manager.updateBookingData(updateData);
            
            // Show floating label for From field
            $('.shuttle-step-1__field--from .shuttle-step-1__floating-label').addClass('show');
            
            // Sync between desktop and mobile
            if (e.target.id === 'shuttle-from-select') {
                this.elements.fromSelectMobile.val(fromId).trigger('change');
            } else {
                this.elements.fromSelectDesktop.val(fromId).trigger('change');
            }
            
            // Load available destinations for "to" dropdown
            await this.updateToDestinations(fromId);
        }
        
        /**
         * Update "to" destinations based on "from" selection
         */
        async updateToDestinations(fromId) {
            try {
                // Show loading state
                this.setToDropdownLoading(true);
                
                // Load available routes
                const routes = await this.manager.loadAvailableRoutes(fromId);
                
                // Clear existing "to" options and optgroups completely
                this.clearDropdownOptions(this.elements.toSelectDesktop);
                this.clearDropdownOptions(this.elements.toSelectMobile);
                
                // Group available destinations by type
                const availableDestinations = [];
                Object.keys(routes).forEach(toId => {
                    const dest = this.findDestinationById(toId);
                    if (dest) {
                        availableDestinations.push(dest);
                    }
                });
                
                // Separate into airports and resorts
                const airports = availableDestinations.filter(dest => this.isAirport(dest)).sort((a, b) => a.label.localeCompare(b.label));
                const resorts = availableDestinations.filter(dest => !this.isAirport(dest)).sort((a, b) => a.label.localeCompare(b.label));
                
                // Add airports group
                if (airports.length > 0) {
                    const airportsLabel = this.translatableStrings?.airports || 'AIRPORTS';
                    const airportsGroup = $(`<optgroup label="${airportsLabel}" data-type="airports"></optgroup>`);
                    airports.forEach(dest => {
                        const option = $(`<option value="${dest.id}" data-type="airport">${dest.label}</option>`);
                        airportsGroup.append(option);
                    });
                    this.elements.toSelectDesktop.append(airportsGroup);
                    this.elements.toSelectMobile.append(airportsGroup.clone());
                }
                
                // Add resorts group
                if (resorts.length > 0) {
                    const resortsLabel = this.translatableStrings?.resorts || 'RESORTS';
                    const resortsGroup = $(`<optgroup label="${resortsLabel}" data-type="resorts"></optgroup>`);
                    resorts.forEach(dest => {
                        const option = $(`<option value="${dest.id}" data-type="resort">${dest.label}</option>`);
                        resortsGroup.append(option);
                    });
                    this.elements.toSelectDesktop.append(resortsGroup);
                    this.elements.toSelectMobile.append(resortsGroup.clone());
                }
                
                // Enable "to" dropdowns
                this.elements.toSelectDesktop.prop('disabled', false);
                this.elements.toSelectMobile.prop('disabled', false);
                
                // Update Select2
                this.elements.toSelectDesktop.trigger('change');
                this.elements.toSelectMobile.trigger('change');
                
            } catch (error) {
                console.error('Failed to update to destinations:', error);
                this.showError('Failed to load available destinations');
            } finally {
                this.setToDropdownLoading(false);
            }
        }
        
        /**
         * Set loading state for "to" dropdown
         */
        setToDropdownLoading(loading) {
            this.form.toggleClass('shuttle-step-1__form--loading', loading);
        }
        
        /**
         * Find destination by ID
         */
        findDestinationById(id) {
            const destinations = this.manager.destinations;
            if (!destinations) return null;
            
            // Search in airports
            const airport = destinations.airports?.find(dest => dest.id === id);
            if (airport) return airport;
            
            // Search in resorts
            const resort = destinations.resorts?.find(dest => dest.id === id);
            if (resort) return resort;
            
            return null;
        }
        
        /**
         * Check if destination is an airport
         */
        isAirport(destination) {
            const destinations = this.manager.destinations;
            if (!destinations || !destination) return false;
            
            return destinations.airports?.some(airport => airport.id === destination.id) || false;
        }
        
        /**
         * Clear dropdown options and optgroups completely
         */
        clearDropdownOptions(selectElement) {
            // Clear all options except the first (placeholder)
            selectElement.find('option:not(:first), optgroup').remove();
            
            // Clear floating label if this is the "To" field
            const isToField = selectElement.attr('id').includes('to');
            if (isToField && !this.isRestoringData) {
                $('.shuttle-step-1__field--to .shuttle-step-1__floating-label').removeClass('show');
                
                // Also clear the booking data for "To" selection
                this.manager.updateBookingData({
                    to: { id: '', label: '' }
                });
                
            }
            
            // Clear Select2's internal data
            if (selectElement.data('select2')) {
                selectElement.select2('destroy');
                
                // Re-initialize Select2 with complete options
                // Get translated placeholder from the first option
                const placeholder = selectElement.find('option:first').text() || 
                                  (selectElement.attr('id').includes('from') ? 'From' : 'To');
                const self = this;
                
                const select2Options = {
                    placeholder: placeholder,
                    allowClear: false,
                    width: '100%',
                    dropdownParent: $(document.body),
                    templateResult: this.formatDestinationOption.bind(this),
                    templateSelection: this.formatDestinationSelection.bind(this),
                    escapeMarkup: function(markup) { return markup; }
                    // dropdownCssClass removed - not supported in this Select2 version
                };
                
                // Re-initialize with all event handlers
                selectElement.select2(select2Options).on('select2:open', function() {
                    self.handleSelect2Open();
                    self.styleOptgroupHeaders();
                    self.setupSearchEventListeners();
                });
            }
        }
        
        /**
         * Handle to location selection
         */
        handleToSelection(e) {
            const toId = e.params.data.id;
            const toLabel = e.params.data.text;
            
            // Clear validation error when user selects to location
            this.hideValidationError();
            
            // Update booking data
            this.manager.updateBookingData({
                to: { id: toId, label: toLabel }
            });
            
            // Show floating label for To field
            $('.shuttle-step-1__field--to .shuttle-step-1__floating-label').addClass('show');
            
            // Sync between desktop and mobile
            if (e.target.id === 'shuttle-to-select') {
                this.elements.toSelectMobile.val(toId).trigger('change');
            } else {
                this.elements.toSelectDesktop.val(toId).trigger('change');
            }
        }
        
        /**
         * Handle date apply (when Apply button is clicked without changing date/time)
         */
        handleDateApply(type, ev, picker) {
            // Get the current date/time from the picker
            const start = picker.startDate;
            
            // Only proceed if we don't already have a value for this field
            const currentValue = type === 'outbound' 
                ? this.manager.bookingData.outboundDate 
                : this.manager.bookingData.returnDate;
            
            // If there's no current value, apply the picker's current date/time
            if (!currentValue) {
                this.handleDateSelection(type, start, null);
            }
        }
        
        /**
         * Validate return date access - prevent opening without outbound date
         */
        validateReturnDateAccess(e) {
            // Only validate for return date inputs (check by ID or name)
            const target = $(e.target);
            const isReturnDateInput = target.attr('id')?.includes('return-date') || 
                                    target.attr('name')?.includes('return_date');
            
            if (!isReturnDateInput) {
                return true;
            }
            
            // Check if outbound date and time are selected
            const outboundDate = this.manager.bookingData.outboundDate;
            const outboundTime = this.manager.bookingData.outboundTime;
            
            if (!outboundDate || !outboundTime) {
                // Prevent the datepicker from opening
                e.preventDefault();
                e.stopPropagation();
                
                // Show error message
                const errorMsg = this.translatableStrings?.errorOutboundDateFirst || 'Please, select outbound date and time first';
                this.showValidationError(errorMsg);
                
                // Blur the input to remove focus
                $(e.target).blur();
                
                return false;
            }
            
            // Clear any existing error if validation passes
            this.hideValidationError();
            return true;
        }
        
        /**
         * Show validation error message
         */
        showValidationError(message) {
            const errorContainer = $('.shuttle-step-1__validation-error');
            const errorText = $('.shuttle-step-1__validation-error-text');
            
            if (errorContainer.length && errorText.length) {
                errorText.text(message);
                errorContainer.show().addClass('show');
                
                // Auto-hide after 5 seconds
                setTimeout(() => {
                    this.hideValidationError();
                }, 5000);
            }
        }
        
        /**
         * Hide validation error message
         */
        hideValidationError() {
            const errorContainer = $('.shuttle-step-1__validation-error');
            errorContainer.hide().removeClass('show');
        }
        
        /**
         * Handle date selection
         */
        handleDateSelection(type, start, end) {
            // Safety net: never accept a disallowed weekday. This covers the
            // "Apply without changing" path where the picker's clamped default
            // could fall on a day the user is not allowed to pick directly.
            const allowedWeekdays = this.manager.getAllowedWeekdays();
            if (start && typeof start.isoWeekday === 'function' && !allowedWeekdays.includes(start.isoWeekday())) {
                const msg = this.translatableStrings?.errorDayNotAvailable || 'Shared economy transfers only run on selected days. Please choose a different date.';
                this.showValidationError(msg);
                return;
            }
            
            const dateStr = start.format('DD.MM.YY'); // Match Figma format
            const timeStr = start.format('HH:mm');
            const fullStr = `${dateStr}, ${timeStr}`;
            
            // Ensure overlay is closed when date is selected
            $('body').removeClass('shuttle-daterangepicker-open');
            
            // Clear validation error when outbound date is selected
            if (type === 'outbound') {
                this.hideValidationError();
            }
            
            // Update booking data
            const updates = {};
            if (type === 'outbound') {
                updates.outboundDate = fullStr;
                updates.outboundTime = timeStr;
                
                // Show floating label for outbound date
                $('.shuttle-step-1__field--outbound-date .shuttle-step-1__date-floating-label').addClass('show');
                $('.shuttle-step-1__input--date[name*="outbound"]').addClass('has-value');
                
                // Update return date picker minimum date to selected outbound date
                this.updateReturnDateMinimum(start);
            } else {
                updates.returnDate = fullStr;
                updates.returnTime = timeStr;
                
                // Show floating label for return date
                $('.shuttle-step-1__field--return-date .shuttle-step-1__date-floating-label').addClass('show');
                $('.shuttle-step-1__input--date[name*="return"]').addClass('has-value');
            }
            
            this.manager.updateBookingData(updates);
            
            // Update input values and sync between desktop/mobile
            if (type === 'outbound') {
                this.elements.outboundDateDesktop.val(fullStr);
                this.elements.outboundDateMobile.val(fullStr);
                this.elements.outboundDateDesktop.addClass('has-value');
                this.elements.outboundDateMobile.addClass('has-value');
            } else {
                this.elements.returnDateDesktop.val(fullStr);
                this.elements.returnDateMobile.val(fullStr);
                this.elements.returnDateDesktop.addClass('has-value');
                this.elements.returnDateMobile.addClass('has-value');
            }
        }
        
        /**
         * Update return date picker minimum date based on outbound date
         */
        updateReturnDateMinimum(outboundDate) {
            // Update return date pickers to have minimum date as 12 hours after the selected outbound date
            const data = this.manager.bookingData;
            const minReturnDate = moment(outboundDate).add(12, 'hours');
            
            // Allowed booking weekdays (ISO 1=Mon..7=Sun)
            const allowedWeekdays = this.manager.getAllowedWeekdays();
            
            // Define booking season end date (15.04.2027)
            const seasonEndDate = moment('2027-04-15', 'YYYY-MM-DD').endOf('day');
            
            // Get translated locale strings from hidden div
            const strings = this.translatableStrings || {};
            
            const returnPickerOptions = {
                locale: { 
                    format: 'DD.MM.YY, HH:mm',
                    firstDay: 1, // Monday as first day of week (0 = Sunday, 1 = Monday)
                    applyLabel: strings.applyLabel || 'Apply',
                    cancelLabel: strings.cancelLabel || 'Cancel',
                    customRangeLabel: strings.customRangeLabel || 'Select time',
                    monthNames: [
                        strings.monthJanuary || 'January',
                        strings.monthFebruary || 'February',
                        strings.monthMarch || 'March',
                        strings.monthApril || 'April',
                        strings.monthMay || 'May',
                        strings.monthJune || 'June',
                        strings.monthJuly || 'July',
                        strings.monthAugust || 'August',
                        strings.monthSeptember || 'September',
                        strings.monthOctober || 'October',
                        strings.monthNovember || 'November',
                        strings.monthDecember || 'December'
                    ],
                    daysOfWeek: [
                        strings.daySu || 'Su',
                        strings.dayMo || 'Mo',
                        strings.dayTu || 'Tu',
                        strings.dayWe || 'We',
                        strings.dayTh || 'Th',
                        strings.dayFr || 'Fr',
                        strings.daySa || 'Sa'
                    ]
                },
                containerClass: 'shuttle-daterangepicker',
                timePicker: true,
                timePicker24Hour: true,
                timePickerIncrement: 15,
                singleDatePicker: true,
                showDropdowns: true,
                minDate: minReturnDate, // Set minimum date to 12h after outbound date
                maxDate: seasonEndDate, // Set maximum date to season end
                autoUpdateInput: false,
                // Read allowed weekdays at render time (not an init-time snapshot)
                isInvalidDate: (date) => !this.manager.getAllowedWeekdays().includes(date.isoWeekday())
            };
            
            // If there's a stored return date, use it as the start date
            if (data.returnDate) {
                const returnMoment = moment(data.returnDate, 'DD.MM.YY, HH:mm');
                if (returnMoment.isValid() && returnMoment.isSameOrAfter(minReturnDate)) {
                    returnPickerOptions.startDate = returnMoment;
                } else {
                    // If stored date is before minimum, use minimum rolled to next allowed weekday, rounded
                    returnPickerOptions.startDate = this.roundToNext15Minutes(this.getNextAllowedMoment(minReturnDate, allowedWeekdays));
                }
            } else {
                // No stored return date - set default to minimum (12h after outbound), rolled to next allowed weekday, rounded
                returnPickerOptions.startDate = this.roundToNext15Minutes(this.getNextAllowedMoment(minReturnDate, allowedWeekdays));
            }
            
            // Destroy and recreate return date pickers with new minimum date
            if (this.elements.returnDateDesktop.data('daterangepicker')) {
                this.elements.returnDateDesktop.data('daterangepicker').remove();
            }
            if (this.elements.returnDateMobile.data('daterangepicker')) {
                this.elements.returnDateMobile.data('daterangepicker').remove();
            }
            
            // Reinitialize with updated options
            this.elements.returnDateDesktop.daterangepicker(returnPickerOptions, this.handleDateSelection.bind(this, 'return'));
            this.elements.returnDateMobile.daterangepicker(returnPickerOptions, this.handleDateSelection.bind(this, 'return'));
            
            // Re-attach apply event handlers for return pickers (they were removed during re-initialization)
            this.elements.returnDateDesktop.on('apply.daterangepicker', this.handleDateApply.bind(this, 'return'));
            this.elements.returnDateMobile.on('apply.daterangepicker', this.handleDateApply.bind(this, 'return'));
            
            // Re-attach show event handlers for return pickers (they were removed during re-initialization)
            this.elements.returnDateDesktop.off('show.daterangepicker'); // Remove old handler if any
            this.elements.returnDateDesktop.on('show.daterangepicker', (event, picker) => {
                this.scrollToDatepicker(picker);
                this.injectSelectTimeLabel(picker);
                this.setupDatepickerLabelObserver(picker);
            });
            
            this.elements.returnDateMobile.off('show.daterangepicker'); // Remove old handler if any
            this.elements.returnDateMobile.on('show.daterangepicker', (event, picker) => {
                this.injectSelectTimeLabel(picker);
                this.setupDatepickerLabelObserver(picker);
            });
            
            // Clear any existing return date if it's before the new minimum (12h after outbound)
            const currentReturnDate = this.manager.bookingData.returnDate;
            if (currentReturnDate) {
                const returnMoment = moment(currentReturnDate, 'DD.MM.YY, HH:mm');
                const minReturnDate = moment(outboundDate).add(12, 'hours');
                if (returnMoment.isBefore(minReturnDate)) {
                    // Clear return date if it's less than 12h after the outbound date
                    this.elements.returnDateDesktop.val('');
                    this.elements.returnDateMobile.val('');
                    $('.shuttle-step-1__field--return-date .shuttle-step-1__date-floating-label').removeClass('show');
                    $('.shuttle-step-1__input--date[name*="return"]').removeClass('has-value');
                    
                    // Update booking data
                    this.manager.updateBookingData({
                        returnDate: '',
                        returnTime: ''
                    });
                }
            }
        }
        
        /**
         * Show passengers modal or popover based on screen size
         */
        showPassengersModal(e) {
            e.preventDefault();
            
            // Update modal with current values
            const { adults, children } = this.manager.bookingData.passengers;
            
            // Update both span and input elements
            this.elements.passengersModal.find('.shuttle-step-1__counter-value[data-type="adults"]').text(adults);
            this.elements.passengersModal.find('.shuttle-step-1__counter-value[data-type="children"]').text(children);
            this.elements.passengersModal.find('input[data-type="adults"]').val(adults);
            this.elements.passengersModal.find('input[data-type="children"]').val(children);
            
            // Update header summary
            this.updatePassengerSummary(adults, children);
            
            // Update button states
            this.updateCounterButtonStates();
            
            // Check if desktop (≥1025px) or mobile
            if (window.innerWidth >= 1025) {
                // Desktop: Show as popover
                this.showDesktopPopover(e);
            } else {
                // Mobile: Show as modal
                this.showMobileModal();
            }
        }
        
        /**
         * Show desktop popover positioned under the clicked button
         */
        showDesktopPopover(e) {
            const clickedButton = $(e.target).closest('.shuttle-step-1__passengers');
            const wrapper = clickedButton.closest('.shuttle-step-1__passengers-wrapper');
            
            // Add popover class and position modal relative to wrapper
            this.elements.passengersModal.addClass('shuttle-step-1__modal--popover');
            
            // Append modal to the wrapper for proper positioning
            wrapper.append(this.elements.passengersModal);
            
            // Show popover
            this.elements.passengersModal.show().addClass('show');
            
            // Set up click outside to close
            this.setupDesktopPopoverCloseHandlers();
        }
        
        /**
         * Show mobile modal (original behavior)
         */
        showMobileModal() {
            // Ensure modal is in body and remove popover class
            this.elements.passengersModal.removeClass('shuttle-step-1__modal--popover');
            if (!this.elements.passengersModal.parent().is('body')) {
                $('body').append(this.elements.passengersModal);
            }
            
            // Show modal
            this.elements.passengersModal.show().addClass('show');
        }
        
        /**
         * Set up handlers to close desktop popover when clicking outside
         */
        setupDesktopPopoverCloseHandlers() {
            // Close on click outside
            $(document).on('click.passenger-popover', (e) => {
                if (!$(e.target).closest('#shuttle-passengers-modal, .shuttle-step-1__passengers').length) {
                    this.hidePassengersModal();
                }
            });
            
            // Close on escape key
            $(document).on('keydown.passenger-popover', (e) => {
                if (e.key === 'Escape') {
                    this.hidePassengersModal();
                }
            });
        }
        
        /**
         * Hide passengers modal or popover
         */
        hidePassengersModal() {
            // Clean up desktop popover event handlers
            $(document).off('click.passenger-popover keydown.passenger-popover');
            
            // Check if it's currently a popover (to avoid moving during animation)
            const isPopover = this.elements.passengersModal.hasClass('shuttle-step-1__modal--popover');
            
            // Remove show class to start fade-out animation
            this.elements.passengersModal.removeClass('show');
            
            setTimeout(() => {
                // Hide the modal
                this.elements.passengersModal.hide();
                
                // Only now remove popover class and move to body (after animation completes)
                if (isPopover) {
                    this.elements.passengersModal.removeClass('shuttle-step-1__modal--popover');
                    
                    // Move back to body for next use
                    if (!this.elements.passengersModal.parent().is('body')) {
                        $('body').append(this.elements.passengersModal);
                    }
                }
            }, 300);
        }
        
        /**
         * Handle passenger count change
         */
        handlePassengerCountChange(e) {
            e.preventDefault();
            
            const button = $(e.target);
            const action = button.data('action');
            const target = button.data('target');
            
            // Get current value from span element (primary source)
            const valueEl = this.elements.passengersModal.find(`.shuttle-step-1__counter-value[data-type="${target}"]`);
            const inputEl = this.elements.passengersModal.find(`input[data-type="${target}"]`);
            
            let currentValue = parseInt(valueEl.text()) || 0;
            
            if (action === 'increase') {
                currentValue++;
            } else if (action === 'decrease' && currentValue > 0) {
                currentValue--;
            }
            
            // Ensure at least 1 adult
            if (target === 'adults' && currentValue < 1) {
                currentValue = 1;
            }
            
            // Update both span and input elements
            valueEl.text(currentValue);
            inputEl.val(currentValue);
            
            // Update header summary
            const adults = parseInt(this.elements.passengersModal.find('.shuttle-step-1__counter-value[data-type="adults"]').text()) || 1;
            const children = parseInt(this.elements.passengersModal.find('.shuttle-step-1__counter-value[data-type="children"]').text()) || 0;
            this.updatePassengerSummary(adults, children);
            
            // Update button states
            this.updateCounterButtonStates();
        }
        
        /**
         * Update passenger summary in modal header
         * Uses smart templates with numeric placeholders for Weglot
         */
        updatePassengerSummary(adults, children) {
            const strings = this.translatableStrings || {};
            let summaryText = '';
            let templateKey = '';
            
            if (adults > 0 && children > 0) {
                // Both adults and children - pick the right template based on singular/plural
                if (adults === 1 && children === 1) {
                    templateKey = 'passengerCount_1adult_1child';
                } else if (adults === 1 && children > 1) {
                    templateKey = 'passengerCount_1adult_children';
                } else if (adults > 1 && children === 1) {
                    templateKey = 'passengerCount_adults_1child';
                } else {
                    templateKey = 'passengerCount_adults_children';
                }
                
                const template = strings[templateKey];
                if (template) {
                    // Replace {0} with adults count, {1} with children count
                    summaryText = template
                        .replace('{0}', adults)
                        .replace('{1}', children);
                } else {
                    // Fallback
                    const adultWord = adults === 1 ? 'adult' : 'adults';
                    const childWord = children === 1 ? 'child' : 'children';
                    summaryText = `${adults} ${adultWord}, ${children} ${childWord}`;
                }
            } else if (adults > 0) {
                // Adults only
                templateKey = adults === 1 ? 'passengerCount_1adult' : 'passengerCount_adults';
                const template = strings[templateKey];
                if (template) {
                    // Replace {0} with adults count
                    summaryText = template.replace('{0}', adults);
                } else {
                    // Fallback
                    const adultWord = adults === 1 ? 'adult' : 'adults';
                    summaryText = `${adults} ${adultWord}`;
                }
            } else {
                // Fallback
                summaryText = strings.passengerCountFallback || '1 adult';
            }
            
            $('#shuttle-passengers-summary').text(summaryText);
        }
        
        /**
         * Update counter button states (disabled/enabled)
         */
        updateCounterButtonStates() {
            const adults = parseInt(this.elements.passengersModal.find('.shuttle-step-1__counter-value[data-type="adults"]').text()) || 1;
            const children = parseInt(this.elements.passengersModal.find('.shuttle-step-1__counter-value[data-type="children"]').text()) || 0;
            
            // Disable adults decrease button if at minimum (1)
            const adultsDecreaseBtn = this.elements.passengersModal.find('[data-action="decrease"][data-target="adults"]');
            if (adults <= 1) {
                adultsDecreaseBtn.prop('disabled', true);
            } else {
                adultsDecreaseBtn.prop('disabled', false);
            }
            
            // Disable children decrease button if at minimum (0)
            const childrenDecreaseBtn = this.elements.passengersModal.find('[data-action="decrease"][data-target="children"]');
            if (children <= 0) {
                childrenDecreaseBtn.prop('disabled', true);
            } else {
                childrenDecreaseBtn.prop('disabled', false);
            }
        }
        
        /**
         * Update passenger button states (legacy method name)
         */
        updatePassengerButtonStates() {
            const adults = parseInt(this.elements.passengersModal.find('[data-type="adults"]').text());
            const children = parseInt(this.elements.passengersModal.find('[data-type="children"]').text());
            
            // Disable decrease button for adults if at minimum
            this.elements.passengersModal.find('[data-target="adults"][data-action="decrease"]').prop('disabled', adults <= 1);
            
            // Disable decrease button for children if at minimum
            this.elements.passengersModal.find('[data-target="children"][data-action="decrease"]').prop('disabled', children <= 0);
        }
        
        /**
         * Apply passenger changes
         */
        applyPassengerChanges() {
            const adults = parseInt(this.elements.passengersModal.find('.shuttle-step-1__counter-value[data-type="adults"]').text()) || 1;
            const children = parseInt(this.elements.passengersModal.find('.shuttle-step-1__counter-value[data-type="children"]').text()) || 0;
            
            // Update booking data
            this.manager.updateBookingData({
                passengers: { adults, children }
            });
            
            // Update passenger display
            this.updatePassengerDisplay();
            
            // Hide modal
            this.hidePassengersModal();
        }
        
        /**
         * Handle direct input changes when user types in the number field
         */
        handlePassengerInputChange(e) {
            const input = $(e.target);
            const target = input.data('type');
            let value = parseInt(input.val()) || 0;
            
            // Validate and constrain the value (only minimum constraints)
            const min = parseInt(input.attr('min')) || 0;
            
            // Apply minimum constraint only
            if (value < min) {
                value = min;
            }
            
            // Special case: adults must be at least 1
            if (target === 'adults' && value < 1) {
                value = 1;
            }
            
            // Update both input and span elements
            input.val(value);
            this.elements.passengersModal.find(`.shuttle-step-1__counter-value[data-type="${target}"]`).text(value);
            
            // Update header summary
            const adults = parseInt(this.elements.passengersModal.find('.shuttle-step-1__counter-value[data-type="adults"]').text()) || 1;
            const children = parseInt(this.elements.passengersModal.find('.shuttle-step-1__counter-value[data-type="children"]').text()) || 0;
            this.updatePassengerSummary(adults, children);
            
            // Update button states
            this.updateCounterButtonStates();
        }
        
        /**
         * Handle keypress events in input fields (Enter key to apply)
         */
        handlePassengerInputKeypress(e) {
            // If Enter key is pressed, trigger blur to validate and then apply
            if (e.which === 13) { // Enter key
                $(e.target).blur();
                // Small delay to ensure blur event is processed first
                setTimeout(() => {
                    this.applyPassengerChanges();
                }, 50);
            }
        }
        
        /**
         * Update passenger display
         */
        updatePassengerDisplay() {
            // Show only the total number, no text labels
            const total = this.manager.bookingData.passengers.adults + this.manager.bookingData.passengers.children;
            $('.shuttle-step-1__passengers-count').text(total);
            
            // Update hidden inputs
            this.elements.passengersDesktop.val(total);
            this.elements.passengersMobile.val(total);
        }
        
        /**
         * Handle swap locations button
         */
        handleSwapLocations(e) {
            e.preventDefault();
            
            const fromValue = this.elements.fromSelectDesktop.val();
            const toValue = this.elements.toSelectDesktop.val();
            
            if (fromValue && toValue) {
                // Swap values
                this.elements.fromSelectDesktop.val(toValue).trigger('select2:select');
                this.elements.toSelectDesktop.val(fromValue).trigger('select2:select');
                
                // Update mobile selects
                this.elements.fromSelectMobile.val(toValue).trigger('select2:select');
                this.elements.toSelectMobile.val(fromValue).trigger('select2:select');
            }
        }
        
        /**
         * Sync current form values to bookingData
         * This ensures ACF prefills or any form values are captured before validation/submission
         */
        syncFormValuesToBookingData() {
            // Get current form values
            const fromId = this.elements.fromSelectDesktop.val();
            const toId = this.elements.toSelectDesktop.val();
            
            // If we have from/to values but they're not in bookingData, add them
            if (fromId && !this.manager.bookingData.from.id) {
                // Find the destination details
                let destinationsArray = [];
                if (Array.isArray(this.manager.destinations)) {
                    destinationsArray = this.manager.destinations;
                } else if (this.manager.destinations && typeof this.manager.destinations === 'object') {
                    destinationsArray = Object.values(this.manager.destinations).flat();
                }
                
                const fromDestination = destinationsArray.find(d => d.id === fromId);
                if (fromDestination) {
                    this.manager.updateBookingData({
                        from: {
                            id: fromDestination.id,
                            label: fromDestination.label || fromDestination.name
                        }
                    });
                }
            }
            
            if (toId && !this.manager.bookingData.to.id) {
                // Find the destination details
                let destinationsArray = [];
                if (Array.isArray(this.manager.destinations)) {
                    destinationsArray = this.manager.destinations;
                } else if (this.manager.destinations && typeof this.manager.destinations === 'object') {
                    destinationsArray = Object.values(this.manager.destinations).flat();
                }
                
                const toDestination = destinationsArray.find(d => d.id === toId);
                if (toDestination) {
                    this.manager.updateBookingData({
                        to: {
                            id: toDestination.id,
                            label: toDestination.label || toDestination.name
                        }
                    });
                }
            }
        }
        
        /**
         * Handle form submission
         */
        async handleFormSubmit(e) {
            e.preventDefault();
            
            try {
                // Before validation, ensure bookingData has the current form values
                // This handles the case where ACF prefills were used but not saved to bookingData
                this.syncFormValuesToBookingData();
                
                // Validate form
                const errors = this.manager.validateStep(1);
                if (errors.length > 0) {
                    // Show validation error using our new error message system
                    this.showValidationError(errors[0]); // Show first error message
                    return;
                }
                
                // Clear any existing validation errors
                this.hideValidationError();
                
                // Track GA event: search_pressed (Row 4)
                if (window.ShuttleGAEvents) {
                    const gaEvents = new window.ShuttleGAEvents();
                    gaEvents.trackSearchPressed(this.manager.bookingData);
                }
                
                // Check if existing route selections are still valid for current parameters
                if (!this.manager.validateRouteSelections()) {
                    // Reset invalid selections
                    this.manager.resetRouteSelections();
                }
                
                // Show loading state
                this.form.addClass('shuttle-step-1__form--loading');
                this.elements.submitButton.prop('disabled', true);
                
                // Update step in booking data
                this.manager.updateBookingData({ step: 2 });
                
                // Navigate to step 2
                await this.manager.goToStep(2);
                
            } catch (error) {
                console.error('Form submission failed:', error);
                const errorMsg = this.translatableStrings?.errorFormSubmitFailed || 'Failed to proceed to next step. Please try again.';
                this.showValidationError(errorMsg);
                
                // Remove loading state
                this.form.removeClass('shuttle-step-1__form--loading');
                this.elements.submitButton.prop('disabled', false);
            }
        }
        
        /**
         * Show error message
         */
        showError(message) {
            // Error handling - can be enhanced with custom modal in the future
            console.error('Shuttle Step 1 Error:', message);
        }
        
        /**
         * Update floating labels based on current field values
         */
        updateFloatingLabels() {
            // Get translated placeholders from the first option (which Weglot has already translated)
            const fromPlaceholder = this.elements.fromSelectDesktop.find('option:first').text() || 'From';
            const toPlaceholder = this.elements.toSelectDesktop.find('option:first').text() || 'To';
            
            // Update Select2 floating labels for From field (desktop and mobile separately)
            this.updateFieldFloatingLabel(this.elements.fromSelectDesktop, fromPlaceholder);
            this.updateFieldFloatingLabel(this.elements.fromSelectMobile, fromPlaceholder);
            
            // Update Select2 floating labels for To field (desktop and mobile separately)
            this.updateFieldFloatingLabel(this.elements.toSelectDesktop, toPlaceholder);
            this.updateFieldFloatingLabel(this.elements.toSelectMobile, toPlaceholder);
            
            // Update date input floating labels (desktop and mobile separately)
            this.updateDateFloatingLabel(this.elements.outboundDateDesktop, 'outbound');
            this.updateDateFloatingLabel(this.elements.outboundDateMobile, 'outbound');
            this.updateDateFloatingLabel(this.elements.returnDateDesktop, 'return');
            this.updateDateFloatingLabel(this.elements.returnDateMobile, 'return');
        }
        
        /**
         * Update floating label for a specific select field
         */
        updateFieldFloatingLabel(selectElement, fieldType) {
            const $field = selectElement.closest('.shuttle-step-1__field--from, .shuttle-step-1__field--to');
            const $rendered = $field.find('.select2-selection__rendered');
            const text = $rendered.text().trim();
            const hasValue = text && text !== fieldType && !text.includes(fieldType);
            
            $field.find('.shuttle-step-1__floating-label').toggleClass('show', hasValue);
        }
        
        /**
         * Update floating label for a specific date field
         */
        updateDateFloatingLabel(dateElement, dateType) {
            if (!dateElement || dateElement.length === 0) return;
            
            const hasValue = dateElement.val().trim() !== '';
            const $field = dateElement.closest('.shuttle-step-1__field--outbound-date, .shuttle-step-1__field--return-date');
            
            dateElement.toggleClass('has-value', hasValue);
            $field.find('.shuttle-step-1__date-floating-label').toggleClass('show', hasValue);
        }
    }
    
    // Initialize when DOM is ready and booking manager is available
    new ShuttleStep1Handler();
});
